import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, ExternalLink } from "lucide-react";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  buildWidgetRows,
  isRunStale,
  MONITOR_STALE_MIN,
  newestRun,
  runAge,
  tallyWidgetVerdicts,
  WIDGET_VERDICT_ORDER,
  widgetVariantLabel,
  type WidgetBrandRow,
  type WidgetCell,
  type WidgetDormantPage,
  type WidgetFinding,
  type WidgetLatest,
  type WidgetLatestPage,
  type WidgetVerdict,
} from "../../../../../../shared/index";

/**
 * The Widgets page — is every production widget page working right now?
 *
 * Everything here is read from what the monitor (apps/b2b-widgets/monitor, a
 * GitHub Actions job: fast lane three times a day, full lane once) wrote to
 * blob; this page never runs a check. BROKEN means a customer sees a broken
 * widget and a GitHub issue went out; THROTTLED means the TipRanks API refused OUR
 * runner; UNVERIFIABLE means the monitor has no credential for that brand yet.
 * Dormant pages (under 100 loads a week) are not tested; they are listed below
 * the table.
 */

const VERDICT_CLASS: Record<WidgetVerdict, string> = {
  BROKEN: "bg-destructive text-destructive-foreground",
  THROTTLED: "bg-slate-500 text-white",
  DEGRADED: "bg-warning text-warning-foreground",
  WARN: "bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-100",
  UNVERIFIABLE: "bg-muted text-muted-foreground",
  OK: "bg-success text-success-foreground",
};

const TABS = [
  { id: "grid", label: "Pages" },
  { id: "runs", label: "Runs" },
  { id: "infra", label: "Infra" },
] as const;

export default function B2bWidgets() {
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") ?? "grid";
  const latest = useQuery({ queryKey: ["b2b", "widgets", "latest"], queryFn: api.b2b.widgets.latest, refetchInterval: 60_000 });
  const fast = latest.data?.runs.fast?.at;
  const full = latest.data?.runs.full?.at;
  const counts = tallyWidgetVerdicts(latest.data ?? null);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">Widgets</h1>
          <p className="text-sm text-muted-foreground">
            Every production widget page on widgets.tipranks.com. Fast lane {runAge(fast)}, full lane {runAge(full)}.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {WIDGET_VERDICT_ORDER.filter((v) => counts[v]).map((v) => (
            <span key={v} className={cn("rounded-full px-2.5 py-1 text-xs font-semibold", VERDICT_CLASS[v])}>
              {counts[v]} {v}
            </span>
          ))}
        </div>
      </div>

      {latest.data && isRunStale(newestRun(fast, full), MONITOR_STALE_MIN) ? (
        <Banner>No monitor run has finished for over 10 hours. The monitor itself may be down: check the Widget Monitor workflow.</Banner>
      ) : null}
      {latest.error ? <Banner>{String((latest.error as Error).message)}</Banner> : null}
      {latest.isSuccess && !latest.data ? (
        <Banner>No results yet. The monitor writes here once its workflow has run with the WIDGET_MONITOR_STORAGE secret.</Banner>
      ) : null}

      <div className="flex gap-1 border-b">
        {TABS.map((t) => (
          <button
            key={t.id}
            className={cn("-mb-px border-b-2 px-4 py-2 text-sm", tab === t.id ? "border-primary font-medium" : "border-transparent text-muted-foreground")}
            onClick={() => setParams((p) => (p.set("tab", t.id), p))}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "grid" ? <Grid latest={latest.data ?? null} loading={latest.isLoading} /> : null}
      {tab === "runs" ? <Runs /> : null}
      {tab === "infra" ? <Infra latest={latest.data ?? null} /> : null}
    </div>
  );
}

function Banner({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex items-start gap-2 rounded-lg border border-warning/50 bg-warning/10 px-4 py-3 text-sm">
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
      <span>{children}</span>
    </div>
  );
}

function Grid({ latest, loading }: { latest: WidgetLatest | null; loading: boolean }) {
  const [onlyProblems, setOnlyProblems] = useState(false);
  const [open, setOpen] = useState<WidgetCell | null>(null);
  const rows = useMemo(() => buildWidgetRows(latest, onlyProblems), [latest, onlyProblems]);

  if (loading) return <p className="text-sm text-muted-foreground">Loading…</p>;
  return (
    <div className="space-y-3">
      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={onlyProblems} onChange={(e) => setOnlyProblems(e.target.checked)} />
        Only pages that are not OK
      </label>
      <div className="rounded-xl border bg-card">
        {rows.map((r) => (
          <BrandLine key={r.brand} row={r} onOpen={setOpen} />
        ))}
      </div>
      <DormantList pages={latest?.dormant ?? []} />
      <PageDrawer cell={open} onClose={() => setOpen(null)} />
    </div>
  );
}

/** Pages the monitor leaves alone, from the registry split of the last run. */
function DormantList({ pages }: { pages: WidgetDormantPage[] }) {
  if (!pages.length) return null;
  return (
    <section className="rounded-xl border bg-card">
      <div className="border-b px-4 py-3">
        <h2 className="font-semibold">Dormant pages, not tested ({pages.length})</h2>
        <p className="text-xs text-muted-foreground">Under 100 loads in the last census week, or pinned dormant in the registry. The monitor does not load them.</p>
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Brand</TableHead>
            <TableHead>Page</TableHead>
            <TableHead>Loads / week</TableHead>
            <TableHead>Why</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {pages.map((p) => (
            <TableRow key={p.key}>
              <TableCell className="text-xs">{p.brand}</TableCell>
              <TableCell className="font-mono text-xs">{p.key.split("/").slice(2).join("/")}</TableCell>
              <TableCell className="text-xs">{p.hits}</TableCell>
              <TableCell className="text-xs text-muted-foreground">{p.pinned ? "pinned dormant" : "low traffic"}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </section>
  );
}

function BrandLine({ row, onOpen }: { row: WidgetBrandRow; onOpen: (c: WidgetCell) => void }) {
  return (
    <div className="grid grid-cols-[10rem_1fr] gap-3 border-b px-4 py-3 last:border-b-0">
      <div>
        <div className="font-medium">{row.brand}</div>
        <div className="text-xs text-muted-foreground">{row.estates.join(" · ")}</div>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {row.cells.map((c) => (
          <button
            key={c.key}
            title={`${c.key} — ${c.worst}${c.page.active ? "" : " (dormant)"}`}
            onClick={() => onOpen(c)}
            className={cn(
              "relative rounded-md px-2 py-1 text-xs font-medium",
              VERDICT_CLASS[c.worst],
              !c.page.active && "opacity-60",
            )}
          >
            {c.label}
            {c.deepNotOk ? <span className="absolute -right-1 -top-1 h-2.5 w-2.5 rounded-full border border-background bg-warning" /> : null}
          </button>
        ))}
      </div>
    </div>
  );
}

function PageDrawer({ cell, onClose }: { cell: WidgetCell | null; onClose: () => void }) {
  const history = useQuery({
    queryKey: ["b2b", "widgets", "history", cell?.key],
    queryFn: () => api.b2b.widgets.history(cell!.key),
    enabled: !!cell,
  });
  const shot = [cell?.page, ...(cell?.variants.map((v) => v.page) ?? [])].find((p) => p?.screenshot)?.screenshot;
  const evidence = useQuery({
    queryKey: ["b2b", "widgets", "evidence", shot],
    queryFn: () => api.b2b.widgets.evidence(shot!),
    enabled: !!shot,
  });

  return (
    <Dialog open={!!cell} onOpenChange={(o) => (o ? null : onClose())}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
        {cell ? (
          <>
            <DialogHeader>
              <DialogTitle className="font-mono text-base">{cell.key}</DialogTitle>
              <DialogDescription>
                {cell.page.active ? "Active" : "Dormant (warn only)"} · {cell.page.verdict} since {new Date(cell.page.since).toLocaleString()}
                {cell.page.alerted ? " · BROKEN issue posted" : ""}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 text-sm">
              <div>
                <div className="mb-1 text-xs font-medium text-muted-foreground">Last {history.data?.length ?? 0} runs, oldest first</div>
                <div className="flex gap-0.5">
                  {(history.data ?? []).map((h, i) => (
                    <span key={i} title={`${h.at} ${h.lane} ${h.verdict}${h.deep ? ` · deep ${h.deep}` : ""}`} className={cn("h-4 w-2 rounded-sm", VERDICT_CLASS[h.verdict])} />
                  ))}
                </div>
              </div>

              <TargetBlock title="Page" targetKey={cell.key} page={cell.page} />
              {cell.variants.map((v) => (
                <TargetBlock key={v.key} title={widgetVariantLabel(v.key)} targetKey={v.key} page={v.page} />
              ))}

              <LivePreview cell={cell} />

              {evidence.data ? (
                <img alt={`Screenshot of ${cell.key}`} className="w-full rounded-md border" src={`data:${evidence.data.contentType};base64,${evidence.data.base64}`} />
              ) : null}
            </div>
          </>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

/**
 * Opens the widget as the customer sees it. The stored URL has its credential
 * redacted, so the API fills in a fresh token or ib handshake first. The tab is
 * opened before the request, inside the click, so a popup blocker allows it.
 */
function OpenWidgetButton({ targetKey, className }: { targetKey: string; className?: string }) {
  const [error, setError] = useState<string | null>(null);
  const open = () => {
    setError(null);
    const tab = window.open("about:blank", "_blank");
    api.b2b.widgets
      .open(targetKey)
      .then(({ url }) => {
        if (!tab) return void window.open(url, "_blank", "noopener");
        tab.opener = null;
        tab.location.href = url;
      })
      .catch((err: unknown) => {
        tab?.close();
        setError(err instanceof Error ? err.message : String(err));
      });
  };
  return (
    <span className={cn("inline-flex items-center gap-2", className)}>
      {error ? <span className="text-xs text-destructive">{error}</span> : null}
      <button className="inline-flex items-center gap-1 text-xs underline" onClick={open} title={`Open ${targetKey} with a fresh credential`}>
        Open <ExternalLink className="h-3 w-3" />
      </button>
    </span>
  );
}

/** The page itself, live, in an iframe: widgets are built to be framed by customers. */
function LivePreview({ cell }: { cell: WidgetCell }) {
  const mobileKey = cell.variants.find((v) => v.key.includes("@mobile"))?.key;
  const [mobile, setMobile] = useState(false);
  const key = mobile && mobileKey ? mobileKey : cell.key;
  const link = useQuery({
    queryKey: ["b2b", "widgets", "open", key],
    queryFn: () => api.b2b.widgets.open(key),
    staleTime: 5 * 60_000,
    retry: false,
  });
  return (
    <div className="rounded-lg border p-3">
      <div className="mb-2 flex items-center gap-2">
        <span className="font-medium">Live widget</span>
        <div className="ml-auto flex gap-1">
          {[false, true].map((m) => (
            <Button key={String(m)} size="sm" variant={mobile === m ? "secondary" : "ghost"} className="h-7 px-2 text-xs" onClick={() => setMobile(m)}>
              {m ? "Mobile 375px" : "Desktop"}
            </Button>
          ))}
        </div>
      </div>
      {link.isError ? (
        <p className="text-xs text-destructive">{link.error instanceof Error ? link.error.message : "Could not open the widget."}</p>
      ) : link.data ? (
        <iframe
          key={link.data.url}
          title={`Live ${key}`}
          src={link.data.url}
          className="mx-auto block h-[640px] rounded-md border bg-white"
          style={{ width: mobile ? 375 : "100%" }}
        />
      ) : (
        <p className="text-xs text-muted-foreground">Loading…</p>
      )}
    </div>
  );
}

function TargetBlock({ title, targetKey, page }: { title: string; targetKey: string; page: WidgetLatestPage }) {
  return (
    <div className="rounded-lg border p-3">
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <span className="font-medium">{title}</span>
        <span className={cn("rounded px-1.5 py-0.5 text-xs font-semibold", VERDICT_CLASS[page.verdict])}>{page.verdict}</span>
        {page.deep ? (
          <span className={cn("rounded px-1.5 py-0.5 text-xs", VERDICT_CLASS[page.deep.verdict])}>deep {page.deep.verdict}</span>
        ) : null}
        <span className="text-xs text-muted-foreground">checked {runAge(page.lastAt)}</span>
        <OpenWidgetButton targetKey={targetKey} className="ml-auto" />
      </div>
      <Findings findings={page.findings} />
      {page.deep?.findings.length ? <Findings findings={page.deep.findings} /> : null}
      {!page.findings.length && !page.deep?.findings.length ? <p className="text-xs text-muted-foreground">No findings.</p> : null}
    </div>
  );
}

function Findings({ findings }: { findings: WidgetFinding[] }) {
  return (
    <ul className="space-y-1">
      {findings.map((f, i) => (
        <li key={i} className="font-mono text-xs">
          <span className="font-semibold">{f.check}</span> · {f.level}
          {f.detail.map((d, j) => (
            <div key={j} className="break-all pl-4 text-muted-foreground">{d}</div>
          ))}
        </li>
      ))}
    </ul>
  );
}

function Runs() {
  const runs = useQuery({ queryKey: ["b2b", "widgets", "runs"], queryFn: () => api.b2b.widgets.runs(100), refetchInterval: 60_000 });
  return (
    <div className="rounded-xl border bg-card">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Run</TableHead>
            <TableHead>Finished</TableHead>
            <TableHead>Took</TableHead>
            <TableHead>Verdicts</TableHead>
            <TableHead>Alerts</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {(runs.data ?? []).map((r) => (
            <TableRow key={r.id}>
              <TableCell className="font-mono text-xs">{r.id}</TableCell>
              <TableCell className="text-xs">{runAge(r.finishedAt)}</TableCell>
              <TableCell className="text-xs">{Math.round((Date.parse(r.finishedAt) - Date.parse(r.startedAt)) / 60_000)} min</TableCell>
              <TableCell>
                <div className="flex flex-wrap gap-1">
                  {WIDGET_VERDICT_ORDER.filter((v) => r.tally[v]).map((v) => (
                    <span key={v} className={cn("rounded px-1.5 py-0.5 text-xs", VERDICT_CLASS[v])}>{r.tally[v]} {v}</span>
                  ))}
                </div>
              </TableCell>
              <TableCell className="text-xs">{r.alerts || ""}</TableCell>
            </TableRow>
          ))}
          {runs.data && !runs.data.length ? (
            <TableRow>
              <TableCell colSpan={5} className="text-sm text-muted-foreground">No runs recorded yet.</TableCell>
            </TableRow>
          ) : null}
        </TableBody>
      </Table>
    </div>
  );
}

/**
 * The regression view for infra changes. Since the 2026-09 cutover two estates
 * share one host: v2 is served by blob, everything else under /content/ and the
 * root-relative fonts and icons by the App Service. A Cloudflare rule that
 * moves a class to the wrong origin shows up here first.
 */
function Infra({ latest }: { latest: WidgetLatest | null }) {
  const assets = Object.entries(latest?.infra?.assets ?? {}).sort();
  const originFindings = Object.entries(latest?.pages ?? {}).flatMap(([key, p]) =>
    p.findings.filter((f) => f.check === "http").flatMap((f) => f.detail.map((d) => ({ key, d }))),
  );
  const unregistered = latest?.drift?.unregistered ?? [];
  const drops = latest?.drift?.drops;
  return (
    <div className="space-y-6">
      <section className="rounded-xl border bg-card">
        <div className="border-b px-4 py-3">
          <h2 className="font-semibold">Who serves the widget assets</h2>
          <p className="text-xs text-muted-foreground">Full lane, {runAge(latest?.infra?.at)}. Requests summed over every page.</p>
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Path class</TableHead>
              <TableHead>Expected</TableHead>
              <TableHead>Blob</TableHead>
              <TableHead>App Service</TableHead>
              <TableHead>Failed</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {assets.map(([cls, c]) => (
              <TableRow key={cls}>
                <TableCell className="font-mono text-xs">{cls}</TableCell>
                <TableCell className="text-xs">{cls.startsWith("/content/v2/") ? "blob" : "App Service"}</TableCell>
                <TableCell className="text-xs">{c.blob}</TableCell>
                <TableCell className="text-xs">{c.appService}</TableCell>
                <TableCell className="text-xs">{c.failed ? <Badge variant="destructive">{c.failed}</Badge> : 0}</TableCell>
              </TableRow>
            ))}
            {!assets.length ? (
              <TableRow>
                <TableCell colSpan={5} className="text-sm text-muted-foreground">No full-lane run yet.</TableCell>
              </TableRow>
            ) : null}
          </TableBody>
        </Table>
      </section>

      <section className="rounded-xl border bg-card p-4">
        <h2 className="mb-2 font-semibold">Page origin and bundle findings</h2>
        {originFindings.length ? (
          <ul className="space-y-1 font-mono text-xs">
            {originFindings.map((f, i) => (
              <li key={i}>
                {f.key} · {f.d}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-muted-foreground">Every page answered 200 from its expected origin with a fresh bundle.</p>
        )}
      </section>

      <section className="rounded-xl border bg-card p-4">
        <h2 className="mb-1 font-semibold">Pages with traffic and no registry entry</h2>
        <p className="mb-2 text-xs text-muted-foreground">
          Weekly Cloudflare census{latest?.drift ? `, ${runAge(latest.drift.censusAt)}` : ""}. Add these to apps/b2b-widgets/monitor/registry/registry.json.
        </p>
        {unregistered.length ? (
          <ul className="space-y-1 font-mono text-xs">
            {unregistered.map((u) => (
              <li key={u.path}>
                {u.path} · {u.hits} hits / 7 days
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-muted-foreground">None.</p>
        )}
      </section>

      <section className="rounded-xl border bg-card p-4">
        <h2 className="mb-1 font-semibold">Pages whose customer loads fell</h2>
        <p className="mb-2 text-xs text-muted-foreground">
          Down more than 30%, and by at least 500 loads a week. Week: the last 7 days vs the 7 before. Month: the last 28 days vs the 28 before. Full lane, {runAge(latest?.runs.full?.at)}.
        </p>
        {drops?.length ? (
          <ul className="space-y-1 font-mono text-xs">
            {drops.map((d) => (
              <li key={`${d.key}:${d.period}`}>
                {d.period} · {d.key} · {d.from} → {d.to} ({(((d.to - d.from) / d.from) * 100).toFixed(1)}%)
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-muted-foreground">{drops ? "None." : "Not measured yet."}</p>
        )}
      </section>
      <Button variant="outline" size="sm" asChild>
        <a href="https://github.com/TipRanks/tipranks-ts-apps/actions/workflows/widget-monitor.yml" target="_blank" rel="noreferrer">
          Widget Monitor workflow <ExternalLink className="ml-1 h-3 w-3" />
        </a>
      </Button>
    </div>
  );
}
